import * as GQL from "src/types/graphql";
import { z } from "zod";

// RuleType corresponds to the type of rule used for display/edit by the view
// not an individual legacy form rule or form engine rule.
export type RuleType =
  | "auto_tag"
  | "connect_forms"
  | "auto_waitlist"
  | "auto_placement"
  | "auto_revoke"
  | "clear_lower_ranked";

export interface RawFormEngineRule {
  id: string;
  rule_type: string;
  rule_config: string;
  key?: string | null;
  version: number;
  active: boolean;
}

export const BaseFormEngineRuleConfigurationSchema = z.object({
  rule_name: z.string().optional(),
  rule_description: z.string().optional(),
  tag_ids: z.array(z.string()).min(1, "At least one tag is required"),
  enable_remove_tag: z.boolean(),
});

export type BaseFormEngineRuleConfiguration = z.infer<
  typeof BaseFormEngineRuleConfigurationSchema
>;

export const DynamicSiblingRuleConfigurationSchema =
  BaseFormEngineRuleConfigurationSchema.extend({
    rule_type: z.literal(GQL.form_engine_rule_type_enum.DYNAMIC_SIBLING_TAG),
    school_ids: z.array(z.string()),
    status: z.array(z.string()).min(1, "At least one status is required"),
  });

export type DynamicSiblingRuleConfiguration = z.infer<
  typeof DynamicSiblingRuleConfigurationSchema
>;

export const QuestionRuleConfigurationSchema =
  BaseFormEngineRuleConfigurationSchema.extend({
    rule_type: z.literal(GQL.form_engine_rule_type_enum.QUESTION_TAG),
    question_id: z.string().uuid(),
    answer_option_ids: z
      .array(z.string())
      .min(1, "At least one answer is required"),
    school_ids: z.array(z.string().uuid()).optional().default([]),
  });

export type QuestionRuleConfiguration = z.infer<
  typeof QuestionRuleConfigurationSchema
>;

export const GeographicPreferenceRuleConfigurationSchema =
  BaseFormEngineRuleConfigurationSchema.extend({
    rule_type: z.literal(
      GQL.form_engine_rule_type_enum.GEOGRAPHIC_PREFERENCE_TAG
    ),
    question_id: z.string().uuid(),
    is_inside: z.boolean(),
    boundary_type: z.union([z.literal("all"), z.literal("selected")]),
    boundary_ids: z.array(z.string()),
    school_ids: z.array(z.string().uuid()).optional(),
  });

export type GeographicPreferenceRuleConfiguration = z.infer<
  typeof GeographicPreferenceRuleConfigurationSchema
>;

export const AutoWaitlistRuleConfigurationSchema = z.object({
  rule_name: z.string().optional(),
  rule_description: z.string().optional(),
  rule_type: z.literal(GQL.form_engine_rule_type_enum.AUTO_WAITLIST),
  source_statuses: z
    .array(z.string())
    .min(1, "At least one status is required"),
  school_ids: z.array(z.string().uuid()).optional().default([]),
  enable_automatic_offers: z.boolean().optional().default(false),
  offer_from_waitlist_only: z.boolean().optional().default(false),
});

export const AutoPlacementRuleConfigurationSchema = z.object({
  rule_name: z.string().optional(),
  rule_description: z.string().optional(),
  rule_type: z.literal(GQL.form_engine_rule_type_enum.AUTO_PLACEMENT),
  school_ids: z.array(z.string().uuid()).optional().default([]),
  tag_ids: z.array(z.string().uuid()).optional().default([]),
  source_statuses: z
    .array(z.string())
    .default([GQL.form_status_enum.Submitted]),
});

export type AutoWaitlistRuleConfiguration = z.infer<
  typeof AutoWaitlistRuleConfigurationSchema
>;

export type AutoPlacementRuleConfiguration = z.infer<
  typeof AutoPlacementRuleConfigurationSchema
>;

export const RuleConfigurationSchema = z.union([
  DynamicSiblingRuleConfigurationSchema,
  QuestionRuleConfigurationSchema,
  GeographicPreferenceRuleConfigurationSchema,
  AutoWaitlistRuleConfigurationSchema,
  AutoPlacementRuleConfigurationSchema,
]);

export type RuleConfiguration = z.infer<typeof RuleConfigurationSchema>;
